import type {
  Credential,
  DuplicateCandidate,
  FieldDefinition,
  Occupancy,
  PersonDetail,
  PersonImage,
  PersonPage,
  RecordActionMethod,
} from '@sentra/contracts';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { hasCode } from '@/api/errors';
import { type PersonRegisterQuery, queryKeys } from '@/api/query-keys';
import { apiRoutes } from '@/api/routes';
import { PROJECTION_CATCH_UP_POLL_MS, withinProjectionCatchUp } from '@/api/write-clock';
import { useApiClient, useSessionActive } from '@/features/auth/session';

/**
 * The reads this application makes, and the one of them whose failure modes are a product
 * requirement rather than an implementation detail.
 *
 * **A search that cannot answer is not a search that found nothing** (AD-23). The projection is an
 * in-memory index the api builds at boot, and until it is ready `GET /api/v1/persons` answers 503
 * with `SYSTEM_SEARCH_INDEX_BUILDING` rather than an empty page — because an empty page is
 * indistinguishable from "this person is not registered", and the operator's next move is to
 * register them again. That produces a second record with a second active pass, which generates
 * phantom anomalies at the gate forever. So the three outcomes are three distinct members of
 * {@link PersonRegisterState} and no screen can collapse them by accident.
 *
 * **A record is never read without saying how it was reached** (AD-53). `person.get` is the one
 * record action with no request body to carry the method, so it travels as a query parameter —
 * which is safe here in a way it is not for a pass code or a name, because the value is one of five
 * closed tokens and says nothing about anybody. {@link usePersonDetail} therefore takes the method
 * as an argument with no default: a caller that did not say how it got here would be recorded as
 * the manual path by the server's own default, and a screen that reached a record by scanning would
 * be counted in the manual-versus-scan ratio on the wrong side.
 */

// Declared in `api/query-keys.ts`, which the eager write-effects table also reads. Re-exported
// so every existing `import { queryKeys } from '@/api/queries'` is unchanged.
export { queryKeys };
export type { PersonRegisterQuery };

/** What the register is doing, as a closed set its screen switches on. */
export type PersonRegisterState =
  | { readonly status: 'loading' }
  /** The index is still building. NOT an empty result, and never rendered as one. */
  | { readonly status: 'index_building' }
  /**
   * The request ran and this page holds nobody.
   *
   * It carries the page anyway, because "nobody matched", "no row passes these filters", "this
   * register is empty" and "that page no longer exists" are four different sentences and only
   * `page`, `pageCount` and `total` can tell them apart.
   */
  | { readonly status: 'empty'; readonly page: PersonPage; readonly refreshing: boolean }
  /** Rows, and whether the answer was cut short and wants narrowing. */
  | { readonly status: 'results'; readonly page: PersonPage; readonly refreshing: boolean }
  | { readonly status: 'failed'; readonly error: unknown };

/**
 * The minimum length a term is worth sending, so one keystroke does not query the register.
 *
 * **Exported because the screen has to say the same number this hook enforces.** The register draws
 * the whole list below a box holding fewer characters than this, and it tells the operator why; two
 * copies that drifted would print "nobody matches this search" for a search that was never made,
 * which is the one sentence that screen's four-way machinery exists to prevent.
 */
export const MIN_SEARCH_LENGTH = 2;

export function usePersonRegister(query: PersonRegisterQuery): PersonRegisterState {
  const client = useApiClient();
  const active = useSessionActive();
  const trimmed = query.term.trim();
  // Below the minimum the box is not a search, so the register is listed and the screen says so.
  const term = trimmed.length >= MIN_SEARCH_LENGTH ? trimmed : '';

  const result = useQuery({
    queryKey: queryKeys.personRegister({ ...query, term }),
    enabled: query.enabled,
    queryFn: () =>
      client.request(apiRoutes.personList, {
        query: {
          // Omitted rather than sent empty: `undefined` is what `appendQuery` drops, and a `q=`
          // present-but-empty is a different filter to the server than no `q` at all.
          q: term.length === 0 ? undefined : term,
          status: query.status,
          country: query.country,
          presence: query.presence,
          photo: query.photo,
          sort: query.sort,
          direction: query.sort === undefined ? undefined : query.direction,
          page: query.page,
          limit: query.limit,
        },
      }),
    // **The page that is on screen stays on screen while the next one is fetched.** Without it
    // every page turn, every column sort and every debounced keystroke unmounts the table and the
    // screen jumps, on the busiest screen in the product. `isPlaceholderData` is what the pager
    // reads to disable itself, so a kept page is never mistaken for a fresh one.
    placeholderData: keepPreviousData,
    // Only for the few seconds after this browser changed a person, and never otherwise: a list
    // nobody has typed into is answered once and left alone. `refetchIntervalInBackground` is left
    // off, so a terminal nobody is looking at asks for nothing — and neither does a locked one,
    // which is refused on this route anyway and has an overlay over the list.
    refetchInterval: () =>
      active && withinProjectionCatchUp() ? PROJECTION_CATCH_UP_POLL_MS : false,
  });

  if (result.isPending) {
    return { status: 'loading' };
  }
  if (result.isError) {
    return hasCode(result.error, 'SYSTEM_SEARCH_INDEX_BUILDING')
      ? { status: 'index_building' }
      : { status: 'failed', error: result.error };
  }
  // `refreshing` is true while the page on screen is one this hook kept rather than one the server
  // has just answered with. The pager reads it to disable itself, so the operator cannot press Next
  // twice for one page.
  const refreshing = result.isPlaceholderData;
  return result.data.items.length === 0
    ? { status: 'empty', page: result.data, refreshing }
    : { status: 'results', page: result.data, refreshing };
}

/** What a read of the live field registry is doing. */
export interface FieldDefinitionsState {
  readonly definitions: readonly FieldDefinition[];
  readonly loading: boolean;
  readonly error: unknown;
}

/**
 * The live field registry the registration form is rendered from (AD-16).
 *
 * Cached for the session with no automatic refetch on focus: an administrator adding a field is a
 * rare, deliberate act, and a form that re-rendered its own shape while somebody was typing into it
 * would lose the value under their cursor. The reload that picks up a new field is the one the
 * operator makes when they open the screen again.
 */
export function useFieldDefinitions(): FieldDefinitionsState {
  const client = useApiClient();
  const result = useQuery({
    queryKey: queryKeys.fieldDefinitions(),
    queryFn: () => client.request(apiRoutes.fieldList, {}),
    staleTime: Number.POSITIVE_INFINITY,
  });

  return {
    definitions: result.data?.items ?? [],
    loading: result.isPending,
    error: result.isError ? result.error : null,
  };
}

/** One whole record, decrypted for a reader who is entitled to it. */
export function usePersonDetail(
  id: string | null,
  method: RecordActionMethod,
): {
  readonly person: PersonDetail | null;
  readonly loading: boolean;
  readonly error: unknown;
} {
  const client = useApiClient();
  const result = useQuery({
    queryKey: queryKeys.personDetail(id ?? ''),
    enabled: id !== null,
    queryFn: () =>
      client.request(apiRoutes.personGet, { params: { id: id ?? '' }, query: { method } }),
    // **Never on window focus**, unlike every other read in this application. `person.get` writes a
    // `person.view_full` audit row on every call, into a table that admits no DELETE, and that row
    // is the one AD-37's exfiltration threshold counts — so a record left open while somebody walks
    // away and comes back would write another row, and another, until the alert fires on an operator
    // who read one record. The provider's default is right for a figure at a gate and wrong here.
    refetchOnWindowFocus: false,
  });

  return {
    person: id === null ? null : (result.data ?? null),
    loading: id !== null && result.isPending,
    error: result.isError ? result.error : null,
  };
}

/** A record's photographs, including the removed rows, which are its history. */
export function usePersonImages(id: string | null): {
  readonly images: readonly PersonImage[];
  readonly loading: boolean;
} {
  const client = useApiClient();
  const result = useQuery({
    queryKey: queryKeys.personImages(id ?? ''),
    enabled: id !== null,
    queryFn: () => client.request(apiRoutes.personImageList, { params: { id: id ?? '' } }),
  });

  return { images: result.data?.items ?? [], loading: id !== null && result.isPending };
}

/**
 * The as-you-type duplicate guard (AD-18).
 *
 * **It asks nothing until there is something to ask about**, which is what `enabled` is doing: an
 * empty form matches everybody, and a request per keystroke from the first letter would make the
 * strongest signal in the product — "this person may already be registered" — arrive as noise that
 * operators learn to ignore. The caller decides what "worth asking" means, because only it knows
 * which fields it has collected.
 *
 * **A failure is silence rather than an error.** This is an advisory shown beside a form that is
 * being filled in; a red banner because the guard could not run would teach an operator that the
 * form is broken when the registration in front of them is perfectly fine. What must never be
 * silent is the opposite case, and it is not: the create response carries the duplicates again.
 */
export function useDuplicateCandidates(
  fields: Readonly<Record<string, unknown>>,
  options: { readonly enabled: boolean; readonly excludePersonId?: string },
): readonly DuplicateCandidate[] {
  const client = useApiClient();
  const result = useQuery({
    queryKey: queryKeys.duplicateCandidates(fields, options.excludePersonId),
    enabled: options.enabled,
    queryFn: () =>
      client.request(apiRoutes.personDuplicateCandidates, {
        body: {
          fields,
          ...(options.excludePersonId === undefined
            ? {}
            : { excludePersonId: options.excludePersonId }),
        },
      }),
  });

  return result.data?.items ?? [];
}

/**
 * The passes one person has held, newest first, as the server orders them.
 *
 * Read by both halves of a record: the view lists them, and the amendment screen lists them and
 * issues into that list. Neither holds the key itself.
 */
export function usePersonCredentials(id: string): {
  readonly credentials: readonly Credential[] | undefined;
  readonly loading: boolean;
} {
  const client = useApiClient();
  const result = useQuery({
    queryKey: queryKeys.personCredentials(id),
    queryFn: () => client.request(apiRoutes.credentialList, { query: { personId: id } }),
  });

  // **`undefined` while the read is in flight, and an array once it has answered.** The difference
  // is what decides whether the amendment screen offers to issue a pass or to replace one: `find`
  // over an absent list answers exactly as a list with no active pass does, so collapsing the two
  // offers the wrong control for as long as the request takes.
  return { credentials: result.data?.items, loading: result.isPending };
}

/** How often the landing screen re-asks for the counts. */
const PRESENCE_COUNTS_INTERVAL_MS = 60_000;

/**
 * The presence counts, refreshed on a timer, for the screen a terminal sits on.
 *
 * **The one polled read in this application, and the route is declared `idle: 'none'` for it.** A
 * route a terminal refreshes by itself is not evidence that anybody is standing at it, so polling
 * one with a finite idle window would silently disable the server's own backstop. The terminal still
 * locks on its own clock — `features/auth/idle.ts` runs on real input and never on requests.
 *
 * A minute, because what it is watching changes on the scale of an excursion: somebody crossing the
 * allowance is a minute-scale event, and a faster poll would buy nothing an operator could act on.
 *
 * It carries counts and no name, which is what makes it safe on a screen nobody is standing at. The
 * list of who is out, with names, is an administration screen.
 */
export function usePresenceCounts(): { readonly counts: Occupancy | null } {
  const client = useApiClient();
  const active = useSessionActive();
  const result = useQuery({
    queryKey: queryKeys.presenceCounts(),
    queryFn: () => client.request(apiRoutes.presenceCounts, {}),
    // **Stopped while the terminal is locked, although this route would answer.** `idle: 'none'`
    // means the poll is not refused here as it is everywhere else — but the card is behind the
    // re-authentication overlay, so the figure is one nobody can read, and the honest thing for an
    // unattended locked terminal to ask for is nothing. The unlock refreshes it.
    refetchInterval: active ? PRESENCE_COUNTS_INTERVAL_MS : false,
    refetchOnWindowFocus: false,
  });
  return { counts: result.data ?? null };
}
